import { TICKET_STATUS_FILTER } from '../constants/ticketStatuses';
import { IDENTIFIER_TYPES } from '../constants/identifierTypes';
import { SORT_OPTIONS } from '../constants/sortOptions';

/**
 * URL Parameter naming convention:
 * - simplrTicketNumber: Simplr ticket number
 * - widgetId: Widget ID
 * - roomId: Room ID
 * - platformTicketId: Platform ticket ID
 * - ticketStatus: Ticket status filter
 * - dateRangeStart: Start date (ISO format)
 * - dateRangeEnd: End date (ISO format)
 * - timeRangeStart: Start time (HH:mm)
 * - timeRangeEnd: End time (HH:mm)
 * - businessIds: Comma-separated business IDs
 * - excludeBusinesses: Boolean flag for exclude mode
 * - brandOptions: JSON-encoded brand options
 */

/**
 * Serialize filter state to URL query parameters
 * @param {Object} filters - Filter state object from getAllFilterParams selector
 * @returns {URLSearchParams} URL parameters object
 */
export const serializeFiltersToUrl = (filters) => {
  const params = new URLSearchParams();

  if (!filters) {
    return params;
  }

  // Handle identifier filters (mutually exclusive)
  // Only one identifier type should be present at a time
  if (filters.simplrTicketNumber) {
    params.set('simplrTicketNumber', filters.simplrTicketNumber);
  } else if (filters.widgetId) {
    params.set('widgetId', filters.widgetId);
  } else if (filters.roomId) {
    params.set('roomId', filters.roomId);
  } else if (filters.platformTicketId) {
    params.set('platformTicketId', filters.platformTicketId);
  }

  if (filters.partialMatch) {
    params.set('partialMatch', 'true');
  }

  if (filters.ticketStatus) {
    params.set('ticketStatus', filters.ticketStatus);
  }

  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }

  if (filters.dateRange) {
    if (filters.dateRange.startDate) {
      const dateStr =
        filters.dateRange.startDate instanceof Date
          ? filters.dateRange.startDate.toISOString()
          : filters.dateRange.startDate;
      params.set('dateRangeStart', dateStr);
    }

    if (filters.dateRange.endDate) {
      const dateStr =
        filters.dateRange.endDate instanceof Date ? filters.dateRange.endDate.toISOString() : filters.dateRange.endDate;
      params.set('dateRangeEnd', dateStr);
    }

    if (filters.dateRange.startTime) {
      params.set('timeRangeStart', filters.dateRange.startTime);
    }

    if (filters.dateRange.endTime) {
      params.set('timeRangeEnd', filters.dateRange.endTime);
    }
  }

  if (filters.businessIdList && filters.businessIdList.length > 0) {
    params.set('businessIds', filters.businessIdList.join(','));
  }

  if (filters.excludeBusinesses === true) {
    params.set('excludeBusinesses', 'true');
  }

  // NOTE(vlad|SPD-14491|2026-03-30): Accept both property names — getAllFilterParams
  // returns brandOptions, while direct callers may use selectedBrandOptions.
  const brandOpts = filters.selectedBrandOptions || filters.brandOptions;
  if (brandOpts && brandOpts.length > 0) {
    try {
      params.set('brandOptions', JSON.stringify(brandOpts));
    } catch (error) {
      console.warn('Failed to serialize brand options:', error);
    }
  }

  return params;
};

/**
 * Deserialize URL query parameters to filter state
 * @param {URLSearchParams|string} searchParams - URL search params or query string
 * @returns {Object} Normalized filter state object
 */
export const deserializeFiltersFromUrl = (searchParams) => {
  let params;

  if (typeof searchParams === 'string') {
    params = new URLSearchParams(searchParams);
  } else if (searchParams instanceof URLSearchParams) {
    params = searchParams;
  } else {
    return {};
  }

  const filters = {};

  // NOTE(vlad|SPD-14096|2026-02-10): Support legacy 'ticket' parameter for backward compatibility
  const ticket = params.get('simplrTicketNumber') || params.get('ticket');
  const widget = params.get('widgetId');
  const room = params.get('roomId');
  const platform = params.get('platformTicketId');

  if (ticket) {
    filters.simplrTicketNumber = ticket;
    filters.activeIdentifierType = IDENTIFIER_TYPES.SIMPLR_TICKET;
  } else if (widget) {
    filters.widgetId = widget;
    filters.activeIdentifierType = IDENTIFIER_TYPES.WIDGET_ID;
  } else if (room) {
    filters.roomId = room;
    filters.activeIdentifierType = IDENTIFIER_TYPES.ROOM_ID;
  } else if (platform) {
    filters.platformTicketId = platform;
    filters.activeIdentifierType = IDENTIFIER_TYPES.PLATFORM_TICKET_ID;
  }

  if (params.get('partialMatch') === 'true') {
    filters.partialMatch = true;
  }

  const status = params.get('ticketStatus');
  if (status && isValidTicketStatus(status)) {
    filters.ticketStatus = status;
  }

  const sortOrder = params.get('sortOrder');
  if (sortOrder && isValidSortOrder(sortOrder)) {
    filters.sortOrder = sortOrder;
  }

  const dateStart = params.get('dateRangeStart');
  const dateEnd = params.get('dateRangeEnd');
  const timeStart = params.get('timeRangeStart');
  const timeEnd = params.get('timeRangeEnd');

  if (dateStart || dateEnd || timeStart || timeEnd) {
    filters.dateRange = {};

    if (dateStart) {
      try {
        filters.dateRange.startDate = new Date(dateStart);
        if (isNaN(filters.dateRange.startDate.getTime())) {
          delete filters.dateRange.startDate;
          console.warn('Invalid dateRangeStart parameter:', dateStart);
        }
      } catch (error) {
        console.warn('Failed to parse dateRangeStart:', error);
      }
    }

    if (dateEnd) {
      try {
        filters.dateRange.endDate = new Date(dateEnd);
        if (isNaN(filters.dateRange.endDate.getTime())) {
          delete filters.dateRange.endDate;
          console.warn('Invalid dateRangeEnd parameter:', dateEnd);
        }
      } catch (error) {
        console.warn('Failed to parse dateRangeEnd:', error);
      }
    }

    if (timeStart) {
      filters.dateRange.startTime = timeStart;
    }

    if (timeEnd) {
      filters.dateRange.endTime = timeEnd;
    }

    if (Object.keys(filters.dateRange).length === 0) {
      delete filters.dateRange;
    }
  }

  const businesses = params.get('businessIds');
  if (businesses) {
    filters.businessIdList = businesses.split(',').filter((id) => id.length > 0);
  }

  const excludeBiz = params.get('excludeBusinesses');
  if (excludeBiz === 'true') {
    filters.excludeBusinesses = true;
  }

  const brands = params.get('brandOptions');
  if (brands) {
    try {
      filters.selectedBrandOptions = JSON.parse(brands);
      if (!Array.isArray(filters.selectedBrandOptions)) {
        delete filters.selectedBrandOptions;
        console.warn('Brand options must be an array');
      }
    } catch (error) {
      console.warn('Failed to parse brand options:', error);
    }
  }

  return filters;
};

/**
 * Validate ticket status value against known statuses
 * @param {string} status - Status value to validate
 * @returns {boolean} True if valid status
 */
const isValidTicketStatus = (status) => {
  const validStatuses = Object.values(TICKET_STATUS_FILTER);

  return validStatuses.includes(status);
};

const isValidSortOrder = (sortOrder) => Object.keys(SORT_OPTIONS).includes(sortOrder);

/**
 * Merge URL parameters with existing search params (preserve non-filter params)
 * @param {string} currentSearch - Current location.search
 * @param {URLSearchParams} filterParams - Filter parameters to merge
 * @returns {string} Complete query string with '?' prefix
 */
export const mergeFilterParams = (currentSearch, filterParams) => {
  const current = new URLSearchParams(currentSearch);
  const merged = new URLSearchParams();

  const filterKeys = new Set([
    'simplrTicketNumber',
    'ticket', // Legacy parameter for backward compatibility
    'widgetId',
    'roomId',
    'platformTicketId',
    // NOTE(vlad|SPD-15655|2026-08-11): Must be scrubbable. Serializer only re-adds partialMatch when
    // the toggle is on; without it in this Set the merge blindly preserves a stale partialMatch=true,
    // so toggle-off + Search never clears it from the URL — and the URL→Redux sync then re-hydrates
    // Redux true, flipping the toggle back on (QA: toggle "ON on UI but OFF actually").
    'partialMatch',
    'ticketStatus',
    'sortOrder',
    'dateRangeStart',
    'dateRangeEnd',
    'timeRangeStart',
    'timeRangeEnd',
    // NOTE(vlad|SPD-14824|2026-05-12): Legacy. Keep in this Set so the merge scrubs it from the
    // URL on the next Search; deserializer already ignores it.
    'timezone',
    'businessIds',
    'excludeBusinesses',
    'brandOptions',
  ]);

  for (const [key, value] of current.entries()) {
    if (!filterKeys.has(key)) {
      merged.set(key, value);
    }
  }

  for (const [key, value] of filterParams.entries()) {
    merged.set(key, value);
  }

  const queryString = merged.toString();

  return queryString ? `?${queryString}` : '';
};

/**
 * Check if URL has any filter parameters
 * @param {string} search - URL search string
 * @returns {boolean} True if URL contains filter parameters
 */
export const hasFilterParams = (search) => {
  const params = new URLSearchParams(search);
  const filterKeys = [
    'simplrTicketNumber',
    'ticket', // Legacy parameter for backward compatibility
    'widgetId',
    'roomId',
    'platformTicketId',
    'ticketStatus',
    'sortOrder',
    'dateRangeStart',
    'dateRangeEnd',
    'timeRangeStart',
    'timeRangeEnd',
    'businessIds',
    'excludeBusinesses',
    'brandOptions',
  ];

  return filterKeys.some((key) => params.has(key));
};
